import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import DramaCard from '@/components/feature/DramaCard';
import JsonLd from '@/components/feature/JsonLd';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { dramas } from '@/mocks/dramas';
import type { Drama } from '@/types/content';

const ALL = '全部';
const categoryKeys = [ALL, ...Array.from(new Set(([...dramas] as Drama[]).map((d) => d.category)))];

type SortKey = 'heat' | 'update' | 'rating';

const SORTS: { key: SortKey; label: string }[] = [
  { key: 'heat', label: '按热度' },
  { key: 'update', label: '按更新进度' },
  { key: 'rating', label: '按评分' },
];

export default function DramasPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCategory = searchParams.get('category') ?? ALL;
  const initialKeyword = searchParams.get('keyword') ?? '';

  const [category, setCategory] = useState(initialCategory);
  const [keyword, setKeyword] = useState(initialKeyword);
  const [sort, setSort] = useState<SortKey>('heat');

  useDocumentMeta({
    title: '短剧库｜全部在追短剧的连续剧集更新顺序与分集剧情介绍 - 爆料短剧',
    description:
      '爆料短剧剧集库按都市逆袭、古装剧场、甜宠恋爱、悬疑推理等主题分类，支持关键词搜索，每部短剧都可查看连续剧集更新顺序、分集剧情介绍与下一集预告。',
    keywords: '短剧库,短剧分类,连续剧集更新顺序,分集剧情介绍,追剧日历',
    path: '/dramas',
    ogType: 'website',
  });

  const list = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    const filtered = ([...dramas] as Drama[]).filter((drama) => {
      const matchCategory = category === ALL || drama.category === category;
      const matchKeyword =
        kw.length === 0 ||
        drama.title.toLowerCase().includes(kw) ||
        drama.subtitle.toLowerCase().includes(kw) ||
        drama.tags.some((tag) => tag.toLowerCase().includes(kw)) ||
        drama.category.toLowerCase().includes(kw);
      return matchCategory && matchKeyword;
    });

    return filtered.sort((a, b) => {
      if (sort === 'rating') return b.rating - a.rating;
      if (sort === 'update') return b.updatedEpisodes / b.totalEpisodes - a.updatedEpisodes / a.totalEpisodes;
      return b.heat - a.heat;
    });
  }, [category, keyword, sort]);

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: '爆料短剧 · 剧集库',
    description: '按主题分类浏览全部在追短剧，查看连续剧集更新顺序与分集剧情介绍。',
    inLanguage: 'zh-CN',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: list.map((drama, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: drama.title,
        url: `https://jinghuibt.cn/drama/${drama.id}`,
      })),
    },
  };

  const handleCategory = (next: string) => {
    setCategory(next);
    const params = new URLSearchParams(searchParams);
    if (next === ALL) {
      params.delete('category');
    } else {
      params.set('category', next);
    }
    setSearchParams(params, { replace: true });
  };

  const handleKeyword = (next: string) => {
    setKeyword(next);
    const params = new URLSearchParams(searchParams);
    if (next.trim() === '') {
      params.delete('keyword');
    } else {
      params.set('keyword', next);
    }
    setSearchParams(params, { replace: true });
  };

  return (
    <SiteLayout>
      <JsonLd data={collectionSchema} />

      <section className="w-full border-b border-background-200 bg-background-100 py-8 md:py-12">
        <div className="mx-auto w-full max-w-content px-4 md:px-6">
          <Breadcrumbs items={[{ label: '剧集库' }]} />
          <h1 className="mt-5 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
            短剧库 · 按顺序追每一部
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-7 text-foreground-600 md:text-base">
            这里收录全站在追的短剧，每部都已整理好连续剧集更新顺序。可以先按主题分类筛选，也可以直接搜索剧名或标签，
            再进入剧集详情页查看分集剧情介绍、下一集预告与全季剧情梗概。
          </p>
        </div>
      </section>

      <section className="w-full bg-background-50 py-10 md:py-14">
        <div className="mx-auto w-full max-w-content px-4 md:px-6">
          <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex w-full max-w-md items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2 focus-within:border-primary-400">
                <i className="ri-search-line text-foreground-400" aria-hidden="true" />
                <input
                  type="search"
                  value={keyword}
                  onChange={(e) => handleKeyword(e.target.value)}
                  placeholder="搜索剧名、标签或分类，如「重生」「悬疑」"
                  aria-label="搜索短剧"
                  className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                />
                {keyword && (
                  <button
                    type="button"
                    aria-label="清空搜索"
                    onClick={() => handleKeyword('')}
                    className="cursor-pointer text-foreground-400 hover:text-foreground-700"
                  >
                    <i className="ri-close-circle-line" aria-hidden="true" />
                  </button>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {SORTS.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setSort(item.key)}
                    className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-2 text-xs transition-colors ${
                      sort === item.key
                        ? 'bg-secondary-900 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2 border-t border-background-200 pt-4">
              {categoryKeys.map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleCategory(key)}
                  className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-1.5 text-xs transition-colors ${
                    category === key
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-700'
                  }`}
                >
                  {key}
                </button>
              ))}
            </div>
          </div>

          <p className="mt-6 text-sm text-foreground-500">
            共找到 <span className="text-primary-600">{list.length}</span> 部短剧
            {category !== ALL && <span> · 分类：{category}</span>}
            {keyword.trim() && <span> · 关键词：{keyword.trim()}</span>}
          </p>

          {list.length > 0 ? (
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {list.map((drama) => (
                <DramaCard key={drama.id} drama={drama} />
              ))}
            </div>
          ) : (
            <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
              <i className="ri-search-eye-line text-4xl text-background-400" aria-hidden="true" />
              <p className="mt-4 text-sm text-foreground-700">没有找到匹配的短剧</p>
              <p className="mt-1 text-xs text-foreground-500">换个关键词或选择「全部」分类再试试</p>
              <button
                type="button"
                onClick={() => {
                  handleKeyword('');
                  handleCategory(ALL);
                }}
                className="mt-5 cursor-pointer rounded-md bg-primary-500 px-5 py-2 text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                重置筛选条件
              </button>
            </div>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}